import { useEffect } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import type { TextFieldProps } from "@apac/form-ui";
import { TextField } from "@apac/form-ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { ActivityFieldValues } from "./hooks";
import { useActivityFields } from "./hooks";
import styles from "./styles.module.scss";

const COMPONENT_KEY = "ActivityDetails";

export type ActivityDetailsFieldOverrides = {
  height?: Partial<TextFieldProps<FieldValues>>;
  weight?: Partial<TextFieldProps<FieldValues>>;
};

interface BookingQuestion {
  id: string;
  label: string;
  required: string;
}

interface ActivityDetailsProps {
  formContext: UseFormReturn<FieldValues, unknown, undefined>;
  fieldOverrides?: ActivityDetailsFieldOverrides;
  bookingQuestions?: BookingQuestion[];
  savedActivityFieldValues?: ActivityFieldValues;
}

export const ActivityDetails: React.FC<ActivityDetailsProps> = ({
  formContext,
  fieldOverrides = {},
  bookingQuestions,
  savedActivityFieldValues,
}) => {
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();

  const { height: heightOverrides = {}, weight: weightOverrides = {} } =
    fieldOverrides;

  const { height, weight } = useActivityFields(
    bookingQuestions,
    savedActivityFieldValues
  );

  useEffect(() => {
    if (height.show && height.value != null && height.value !== "") {
      formContext.setValue("height", height.value);
    }
    if (weight.show && weight.value != null && weight.value !== "") {
      formContext.setValue("weight", weight.value);
    }
  }, [formContext, height.show, height.value, weight.show, weight.value]);

  const getUnitLabel = (unit: string | undefined) => {
    if (!unit) return "";
    switch (unit) {
      case "ft":
        return t("travelerForm.activityDetails.unitFt");
      case "lbs":
        return t("travelerForm.activityDetails.unitLbs");
      case "cm":
        return t("travelerForm.activityDetails.unitCm");
      case "kg":
        return t("travelerForm.activityDetails.unitKg");
      default:
        return unit;
    }
  };

  if (!height.show && !weight.show) {
    return null;
  }

  return (
    <div className={block}>
      <div className={cn("header")}>
        <h3 className={cn("title")}>
          {t("travelerForm.activityDetails.title")}
        </h3>
        <p className={cn("subtitle")}>
          {t("travelerForm.activityDetails.subtitle")}
        </p>
      </div>

      <div className={cn("fields")}>
        {height.show && (
          <TextField
            formContext={formContext}
            name="height"
            label={t("travelerForm.activityDetails.heightLabel", {
              unit: getUnitLabel(height.unit),
            })}
            required={height.required}
            placeholder={t("travelerForm.activityDetails.heightPlaceholder")}
            {...heightOverrides}
          />
        )}

        {weight.show && (
          <TextField
            formContext={formContext}
            name="weight"
            label={t("travelerForm.activityDetails.weightLabel", {
              unit: getUnitLabel(weight.unit),
            })}
            required={weight.required}
            placeholder={t("travelerForm.activityDetails.weightPlaceholder")}
            {...weightOverrides}
          />
        )}
      </div>
    </div>
  );
};
